import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { ref, get } from "firebase/database";
import { useNavigate, useParams } from "react-router-dom";
import DashboardHome from "../components/DashboardHome";
import AuctionSettings from "../components/AuctionSettings";
import PlayerList from "../components/PlayerList";
import TeamList from "../components/TeamList";
import OrganizerAuctionControl from "./OrganizerAuctionControl";
import Popup from "../components/Popup";


const OrganizerDashboard = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        // Check if user is organizer
        const userRef = ref(db, `users/${user.uid}`);
        const userSnapshot = await get(userRef);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          
          if (userData.role !== "organizer") {
            setPopup({
              isOpen: true,
              title: 'Access Denied',
              message: "Access denied. Organizer only.",
              type: 'error'
            });
            navigate("/");
            return;
          }
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        setPopup({
          isOpen: true,
          title: 'Load Failed',
          message: "Error loading dashboard: " + error.message,
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!gameId) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          No game found. Please contact support.
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" >
      <div className="container-fluid pt-2 px-4">
        <div className="card shadow-lg border-0" style={{ borderRadius: "20px", overflow: "hidden" }}>
          {/* Hero Banner */}
          <div className="card-header text-white text-center py-4" style={{ background: "#667eea"}}>
            <h1 className="h2 fw-bold mb-2">
              <i className="bi bi-speedometer2 me-2"></i>
              Organizer Dashboard
            </h1>
            <p className="mb-0">Manage your auction, players, and teams</p>
          </div>

          <div className="card-body p-4">
            {/* Tab Navigation */}
            <ul className="nav nav-pills mb-4 justify-content-center" style={{ gap: "10px" }}>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === 'overview' ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === 'overview' ? "none" : "2px solid #dee2e6",
                    color: activeTab === 'overview' ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-info-circle me-2"></i>
                  Overview
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('settings')}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === 'settings' ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === 'settings' ? "none" : "2px solid #dee2e6",
                    color: activeTab === 'settings' ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-gear me-2"></i>
                  Settings
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'players' ? 'active' : ''}`}
                  onClick={() => setActiveTab('players')}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === 'players' ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === 'players' ? "none" : "2px solid #dee2e6",
                    color: activeTab === 'players' ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-people me-2"></i>
                  Players
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'teams' ? 'active' : ''}`}
                  onClick={() => setActiveTab('teams')}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === 'teams' ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === 'teams' ? "none" : "2px solid #dee2e6",
                    color: activeTab === 'teams' ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-shield me-2"></i>
                  Teams
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'auctioncontrol' ? 'active' : ''}`}
                  onClick={() => setActiveTab('auctioncontrol')}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === 'auctioncontrol' ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === 'auctioncontrol' ? "none" : "2px solid #dee2e6",
                    color: activeTab === 'auctioncontrol' ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-broadcast me-2"></i>
                  Auction Control
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'overview' && (
                <div className="animate-fade-in">
                  <DashboardHome gameId={gameId} />
                </div>
              )}
              {activeTab === 'settings' && (
                <div className="animate-fade-in">
                  <AuctionSettings gameId={gameId} />
                </div>
              )}
              {activeTab === 'players' && (
                <div className="animate-fade-in">
                  <PlayerList gameId={gameId} />
                </div>
              )}
              {activeTab === 'teams' && (
                <div className="animate-fade-in">
                  <TeamList gameId={gameId} />
                </div>
              )}
              {activeTab === 'auctioncontrol' && (
                <div className="animate-fade-in">
                  <OrganizerAuctionControl gameId={gameId} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .animate-fade-in {
          animation: fadeIn 0.5s ease-in;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .nav-link {
          transition: all 0.3s ease;
        }

        .nav-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          transform: translateY(-2px);
        }
      `}</style>

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />
    </div>
  );
};

export default OrganizerDashboard;